import axios from "axios";
import { io, Socket } from "socket.io-client";
import {
  Queue,
  QueueParticipant,
  QueueMetrics,
  QueueStatus,
} from "@shared/types";
import { unifiedStorage } from "@/lib/unified-storage";
import { authEventBus } from "@/lib/auth-event-bus";

const API_URL = import.meta.env.VITE_QUEUE_API;
const WS_URL = import.meta.env.VITE_WS_URL || import.meta.env.VITE_QUEUE_API;

class QueueService {
  private api = axios.create({
    baseURL: API_URL,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });
  private socket: Socket | null = null;
  private socketConnections: Map<string, () => void> = new Map();
  private activeSubscriptions: Set<string> = new Set();
  private authEventUnsubscribe: (() => void) | null = null;

  constructor() {
    // Listen for auth events to reconnect WebSocket
    this.setupAuthEventListeners();
    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      // Get token from unifiedStorage (single source of truth)
      const tokens = unifiedStorage.getTokens();
      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
        console.log("[Queue Service] Using token from unifiedStorage");
      } else {
        console.warn("[Queue Service] No token found in unifiedStorage");
      }

      // Get eventId from unifiedStorage
      const eventId = unifiedStorage.getEventId();
      if (eventId) {
        // Add eventId as a header for backend validation
        config.headers["X-Event-Id"] = eventId;
        console.log(
          "[Queue Service] Using eventId from unifiedStorage:",
          eventId
        );
      } else {
        console.warn("[Queue Service] No eventId found in unifiedStorage");
      }

      return config;
    });

    // Add response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.error("[Queue Service] Authentication error - 401");
          // Don't clear localStorage here - let useAuth handle it
          // localStorage.removeItem('auth_tokens');
          // localStorage.removeItem('current_event_id');
          // Don't redirect - let the app handle it
          // window.location.href = dooEventsFrontend;
        }
        return Promise.reject(error);
      }
    );
  }

  // Queue Management
  async getQueues(eventId: string): Promise<Queue[]> {
    const response = await this.api.get(`/queues`, { params: { eventId } });
    return response.data;
  }

  async getQueue(queueId: string): Promise<Queue> {
    const response = await this.api.get(`/queues/${queueId}`);
    return response.data;
  }

  async createQueue(queue: Partial<Queue>): Promise<Queue> {
    const response = await this.api.post("/queues", queue);
    return response.data;
  }

  async updateQueue(queueId: string, queue: Partial<Queue>): Promise<Queue> {
    const response = await this.api.put(`/queues/${queueId}`, queue);
    return response.data;
  }

  async deleteQueue(queueId: string): Promise<void> {
    await this.api.delete(`/queues/${queueId}`);
  }

  async updateQueueStatus(queueId: string, status: string): Promise<void> {
    await this.api.patch(`/queues/${queueId}/status`, { status });
  }

  // Simultaneous Attendance Management
  async updateQueueSettings(queueId: string, settings: {
    simultaneousAttendance?: number;
    autoAdvance?: boolean;
    averageServiceTimeMinutes?: number;
    maxQueueSize?: number;
    allowRejoin?: boolean;
  }): Promise<Queue> {
    const response = await this.api.patch(`/queues/${queueId}/settings`, settings);
    return response.data;
  }

  async processNextBatch(queueId: string): Promise<{
    processedCount: number;
    entries: Array<{
      entryId: string;
      userId: string;
      position: number;
      status: string;
    }>;
  }> {
    const response = await this.api.post(`/queues/${queueId}/process-next`);
    return response.data;
  }

  async markAsServing(entryId: string): Promise<void> {
    await this.api.patch(`/entries/${entryId}/serve`);
  }

  async completeService(entryId: string, notes?: string): Promise<{
    status: string;
    autoAdvanced: boolean;
    nextEntriesCalled: number;
  }> {
    const response = await this.api.post(`/entries/${entryId}/complete`, { notes });
    return response.data;
  }

  async getServingStatus(queueId: string): Promise<{
    totalWaiting: number;
    currentlyServing: number;
    currentlyCalled: number;
    simultaneousAttendance: number;
    effectiveQueueLength: number;
  }> {
    const response = await this.api.get(`/queues/${queueId}/serving-status`);
    return response.data;
  }

  async getEffectivePosition(entryId: string): Promise<{
    position: number;
    effectivePosition: number;
    estimatedWaitTime: string;
    estimatedBatches: number;
    simultaneousAttendance: number;
  }> {
    const response = await this.api.get(`/entries/${entryId}/position`);
    return response.data;
  }

  // Participant Management
  async getQueueParticipants(
    queueId: string,
    status?: QueueStatus | ""
  ): Promise<QueueParticipant[]> {
    const params: any = {};
    if (status) params.status = status;

    const response = await this.api.get(`/queues/${queueId}/participants`, {
      params,
    });
    return response.data;
  }

  async addParticipant(
    queueId: string,
    userId: string,
    metadata?: any
  ): Promise<QueueParticipant> {
    const response = await this.api.post(`/queues/${queueId}/participants`, {
      userId,
      metadata,
    });
    return response.data;
  }

  async checkInParticipant(participantId: string): Promise<void> {
    await this.api.post(`/participants/${participantId}/check-in`);
  }

  async checkOutParticipant(participantId: string): Promise<void> {
    await this.api.post(`/participants/${participantId}/check-out`);
  }

  async updateParticipantStatus(
    participantId: string,
    status: QueueStatus
  ): Promise<void> {
    await this.api.patch(`/participants/${participantId}/status`, { status });
  }

  async removeParticipant(participantId: string): Promise<void> {
    await this.api.delete(`/participants/${participantId}`);
  }

  // Metrics
  async getMetrics(
    eventId: string,
    startDate: Date,
    endDate: Date
  ): Promise<QueueMetrics[]> {
    const response = await this.api.get("/metrics", {
      params: {
        eventId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });
    return response.data;
  }

  async getQueueMetrics(queueId: string): Promise<QueueMetrics> {
    const response = await this.api.get(`/queues/${queueId}/metrics`);
    return response.data;
  }

  // Export
  async exportParticipants(queueId: string): Promise<string> {
    const response = await this.api.get(`/queues/${queueId}/export`, {
      responseType: "text",
    });
    return response.data;
  }

  // Custom Fields Management
  async getCustomFieldsConfig(queueId: string): Promise<any[]> {
    const response = await this.api.get(`/queues/${queueId}/custom-fields`);
    return response.data;
  }

  async updateCustomFieldsConfig(queueId: string, customFieldsConfig: any[]): Promise<Queue> {
    const response = await this.api.put(`/queues/${queueId}/custom-fields`, {
      customFieldsConfig
    });
    return response.data;
  }

  // Real-time updates (WebSocket)
  connectToQueueUpdates(
    queueId: string,
    onUpdate: (data: any) => void,
    options?: {
      onBatchCalled?: (data: {
        queueId: string;
        entries: Array<{ entryId: string; userId: string; position: number }>;
        count: number;
      }) => void;
      onSettingsUpdated?: (data: {
        queueId: string;
        settings: any;
      }) => void;
      onMultipleServing?: (data: {
        queueId: string;
        servingEntries: Array<{ entryId: string; userId: string }>;
        totalServing: number;
      }) => void;
    }
  ): () => void {
    // Initialize socket if not already connected
    if (!this.socket) {
      // Get auth data from unifiedStorage
      const tokens = unifiedStorage.getTokens();
      const eventId = unifiedStorage.getEventId();

      if (!tokens?.accessToken) {
        console.error("[Queue Service] No auth token for WebSocket connection");
      }

      if (!eventId) {
        console.warn("[Queue Service] No eventId for WebSocket connection");
      }

      this.socket = io(WS_URL, {
        auth: {
          token: tokens?.accessToken || null,
          eventId: eventId,
        },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      this.socket.on("connect", () => {
        console.log("WebSocket connected");
      });

      this.socket.on("disconnect", () => {
        console.log("WebSocket disconnected");
      });

      this.socket.on("error", (error) => {
        console.error("WebSocket error:", error);
      });
    }

    // Subscribe to queue updates
    this.socket.emit("subscribe", { queueId });
    this.trackSubscription(queueId);

    // Listen for updates
    const eventName = `queue:${queueId}:update`;
    this.socket.on(eventName, onUpdate);

    // Listen for batch events if handlers provided
    const batchEventName = `queue.batch.called`;
    const settingsEventName = `queue.settings.updated`;
    const multipleServingEventName = `queue.multiple.serving`;

    if (options?.onBatchCalled) {
      this.socket.on(batchEventName, (data) => {
        if (data.queueId === queueId) {
          options.onBatchCalled!(data);
        }
      });
    }

    if (options?.onSettingsUpdated) {
      this.socket.on(settingsEventName, (data) => {
        if (data.queueId === queueId) {
          options.onSettingsUpdated!(data);
        }
      });
    }

    if (options?.onMultipleServing) {
      this.socket.on(multipleServingEventName, (data) => {
        if (data.queueId === queueId) {
          options.onMultipleServing!(data);
        }
      });
    }

    // Return cleanup function
    const cleanup = () => {
      if (this.socket) {
        this.socket.emit("unsubscribe", { queueId });
        this.socket.off(eventName, onUpdate);

        // Remove batch event listeners
        if (options?.onBatchCalled) {
          this.socket.off(batchEventName);
        }
        if (options?.onSettingsUpdated) {
          this.socket.off(settingsEventName);
        }
        if (options?.onMultipleServing) {
          this.socket.off(multipleServingEventName);
        }

        // Untrack subscription
        this.untrackSubscription(queueId);

        // If no more connections, disconnect socket
        this.socketConnections.delete(queueId);
        if (this.socketConnections.size === 0) {
          this.socket.disconnect();
          this.socket = null;
        }
      }
    };

    this.socketConnections.set(queueId, cleanup);
    return cleanup;
  }

  // Disconnect all WebSocket connections
  disconnectAll(): void {
    this.socketConnections.forEach((cleanup) => cleanup());
    this.socketConnections.clear();
    this.activeSubscriptions.clear();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // Setup auth event listeners for WebSocket reconnection
  private setupAuthEventListeners(): void {
    // Clean up previous listener if exists
    if (this.authEventUnsubscribe) {
      this.authEventUnsubscribe();
    }

    // Listen for token updates
    this.authEventUnsubscribe = authEventBus.on("token-updated", (data) => {
      console.log(
        "[Queue Service] Token updated, reconnecting WebSocket...",
        data
      );
      this.reconnectWebSocket();
    });

    // Also listen for eventId changes
    authEventBus.on("eventId-changed", (data) => {
      console.log(
        "[Queue Service] EventId changed, reconnecting WebSocket...",
        data
      );
      this.reconnectWebSocket();
    });

    // Listen for logout to disconnect
    authEventBus.on("logout", () => {
      console.log(
        "[Queue Service] Logout detected, disconnecting WebSocket..."
      );
      this.disconnectAll();
    });
  }

  // Reconnect WebSocket with new auth data
  private reconnectWebSocket(): void {
    if (!this.socket) {
      console.log("[Queue Service] No active WebSocket to reconnect");
      return;
    }

    console.log("[Queue Service] Reconnecting WebSocket with updated auth...");

    // Save active subscriptions
    const subscriptions = Array.from(this.activeSubscriptions);

    // Disconnect current socket
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Clear connections but keep subscription list
    this.socketConnections.clear();

    // Reconnect will happen on next connectToQueueUpdates call
    // Force reconnect for active subscriptions
    if (subscriptions.length > 0) {
      console.log("[Queue Service] Re-subscribing to queues:", subscriptions);
      // The next call to connectToQueueUpdates will create new socket with updated auth
    }
  }

  // Track active subscriptions
  private trackSubscription(queueId: string): void {
    this.activeSubscriptions.add(queueId);
  }

  private untrackSubscription(queueId: string): void {
    this.activeSubscriptions.delete(queueId);
  }
}

export const queueService = new QueueService();
